import VMAP from '@dailymotion/vmap';
import * as VAST from '@dailymotion/vast-client';
import { nanoid } from 'nanoid/non-secure';
import { groupBy } from 'lodash';
import { Manifest } from '../enums';
const vastClient = new VAST.VASTClient();
/**
 * Fetches and parses an XML document from a URL.
 */
const getXMLDocument = async (url) => {
    try {
        const response = await fetch(url);
        const str = await response.text();
        return new DOMParser().parseFromString(str, 'text/xml');
    }
    catch (error) {
        console.log('getXMLDocument', error);
        throw error;
    }
};
/**
 * Parses a time offset string (e.g., "00:01:30") into a number in seconds of seconds.
 */
const parseTimeOffset = (offset) => {
    const [hours, minutes, seconds] = offset.split(':');
    const timestamp = Number(seconds) + (Number(minutes) * 60) + (Number(hours) * 60 * 60);
    return timestamp;
};
/**
 * Parses an ads container element and extracts ad URLs.
 */
const parseAdsContainer = (container) => {
    const ads = Array.from(container.getElementsByTagName('Ad'));
    return ads.map((adElement) => adElement.textContent ?? '');
};
/**
 * Parses a midroll element into a MidrollInfo object.
 */
const parseMidroll = (midrollElement) => {
    const offset = midrollElement.getAttribute('timeOffset');
    return {
        ads: parseAdsContainer(midrollElement),
        timestamp: offset ? parseTimeOffset(offset) : 0,
    };
};
/**
 * Parses a playlist XML document into a PlaylistManifest object.
 */
const parsePlaylist = (xml) => {
    const prerolls = Array.from(xml.getElementsByTagName('Preroll'));
    const midrolls = Array.from(xml.getElementsByTagName('Midroll'));
    return {
        type: Manifest.PLAYLIST,
        prerolls: prerolls.flatMap(parseAdsContainer),
        midrolls: midrolls.map(parseMidroll),
    };
};
/**
 * Parses a VMAP XML document into a PlaylistManifest object.
 */
const parseVMAP = (xml) => {
    const prerolls = [];
    const midrolls = [];
    const vmap = new VMAP(xml);
    const adsById = groupBy(vmap.adBreaks, 'breakId');
    Object.entries(adsById).forEach(([, adBreaks]) => {
        const urls = adBreaks.map((info) => {
            return info.adSource.adTagURI;
        });
        if (!adBreaks[0]) {
            return;
        }
        const { timeOffset } = adBreaks[0];
        if (timeOffset === 'start') {
            prerolls.push(...urls);
        }
        else if (timeOffset !== 'end') {
            midrolls.push({
                ads: urls,
                timestamp: parseTimeOffset(timeOffset),
            });
        }
    });
    return {
        type: Manifest.PLAYLIST,
        prerolls,
        midrolls,
    };
};
const findFirstElement = (node) => {
    if (node.tagName) {
        return node;
    }
    let sibling = node.nextSibling;
    while (sibling) {
        if (sibling.tagName) {
            return sibling;
        }
        sibling = sibling.nextSibling;
    }
    if (node.ownerDocument?.documentElement) {
        return node.ownerDocument.documentElement;
    }
    return null;
};
/**
 * Loads and parses an ads manifest from a URL.
 */
export const loadManifest = async (url) => {
    console.log('loadManifest', url);
    const xml = await getXMLDocument(url);
    const container = findFirstElement(xml.documentElement);
    switch (container?.tagName) {
        case 'Playlist':
            return parsePlaylist(xml);
        case 'vmap:VMAP':
            return parseVMAP(xml);
        case 'VAST': {
            const info = await vastClient.parseVAST(xml);
            return {
                type: Manifest.VAST,
                ads: info.ads,
            };
        }
        default: {
            return Promise.reject(new Error('Unknown ads manifest'));
        }
    }
};
/**
 * Loads VAST ads from a URL.
 */
const loadVAST = async (url) => {
    const manifest = await loadManifest(url.trim());
    switch (manifest.type) {
        case Manifest.VAST:
            return Promise.resolve(manifest.ads);
        default:
            // TODO: Recursive playlists should be added here?
            return Promise.resolve([]);
    }
};
/**
 * Loads a list of VAST ads from multiple URLs.
 * Flattened array of VAST ads.
 */
export const loadVASTList = async (ads) => {
    const queue = ads.map(loadVAST);
    const result = await Promise.all(queue);
    return result.flat();
};
/**
 * Checks if a VAST URL is a public ad URL.
 */
export const isPubAdUrl = (vastUrl) => {
    return vastUrl.includes('pubads.g.doubleclick.net');
};
/**
 * Creates an AdBreakInfo object from a VideoAdInfo object.
 */
export const createAdBreakInfo = (adBreak) => {
    return {
        id: nanoid(),
        ...adBreak,
    };
};
